/**
 * Runtime theme applier.
 *
 * Converts a `ThemePack` into CSS-variable mutations on `:root`. The values
 * are the same shape Tailwind already consumes (e.g. `--background: 0 0% 100%`),
 * so existing utility classes like `bg-background` keep working with no
 * rebuild required.
 *
 * Goals
 * -----
 *  - Idempotent: applying the same pack twice produces the same DOM state.
 *  - Reversible: `resetTheme()` removes every override and falls back to the
 *    rules in `index.css`.
 *  - First-paint friendly: a sibling helper (`first-paint.ts`) inlines the
 *    same logic into `index.html` so a saved theme is honored before React
 *    boots — no flash of default theme.
 *
 * Out of scope here: persistence, contrast validation, AI generation. Those
 * live in their own modules so this file stays a pure DOM mutator.
 */
import type { ChartTokens, ColorTokens, ResolvedMode, ThemePack } from "@/theme/types";

/**
 * Variable mapping table — single source of truth for token name → CSS var.
 * Exported so tests can iterate it without re-deriving the names.
 */
export const COLOR_VAR_MAP: Record<keyof ColorTokens, string> = {
  background: "--background",
  foreground: "--foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  popover: "--popover",
  popoverForeground: "--popover-foreground",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  destructive: "--destructive",
  destructiveForeground: "--destructive-foreground",
  success: "--success",
  successForeground: "--success-foreground",
  successMuted: "--success-muted",
  successMutedForeground: "--success-muted-foreground",
  successBorder: "--success-border",
  warning: "--warning",
  warningForeground: "--warning-foreground",
  warningMuted: "--warning-muted",
  warningMutedForeground: "--warning-muted-foreground",
  warningBorder: "--warning-border",
  info: "--info",
  infoForeground: "--info-foreground",
  infoMuted: "--info-muted",
  infoMutedForeground: "--info-muted-foreground",
  infoBorder: "--info-border",
  border: "--border",
  input: "--input",
  ring: "--ring",
  sidebarBackground: "--sidebar-background",
  sidebarForeground: "--sidebar-foreground",
  sidebarPrimary: "--sidebar-primary",
  sidebarPrimaryForeground: "--sidebar-primary-foreground",
  sidebarAccent: "--sidebar-accent",
  sidebarAccentForeground: "--sidebar-accent-foreground",
  sidebarBorder: "--sidebar-border",
  sidebarRing: "--sidebar-ring",
  // Brand vars are NEW — they don't exist in `index.css` yet but we register
  // them in `tailwind.config.ts` (separate edit) so `bg-brand-background`
  // resolves to `hsl(var(--brand-background))`.
  brandBackground: "--brand-background",
  brandMain: "--brand-main",
  brandAuxiliary1: "--brand-auxiliary1",
  brandAuxiliary2: "--brand-auxiliary2",
};

export const CHART_VAR_MAP: Record<keyof ChartTokens, string> = {
  chart1: "--chart-1",
  chart2: "--chart-2",
  chart3: "--chart-3",
  chart4: "--chart-4",
  chart5: "--chart-5",
  chart6: "--chart-6",
  chart7: "--chart-7",
  chart8: "--chart-8",
  chart9: "--chart-9",
  chart10: "--chart-10",
  chart11: "--chart-11",
  chart12: "--chart-12",
};

/** ID we tag our managed Google Fonts <link> with so we can dedupe / replace. */
const FONTS_LINK_ID = "lovable-theme-fonts";

/** ID we tag the favicon <link> we manage at runtime. */
const FAVICON_LINK_ID = "lovable-theme-favicon";

/**
 * Resolve which mode's tokens to apply. If the requested mode is not present
 * (e.g. theme is light-only but the OS / user picked dark), we fall back to
 * the available mode rather than crash — surface that decision via the return
 * value so callers can warn or update UI.
 */
export function resolveMode(pack: ThemePack, requested: ResolvedMode): ResolvedMode {
  if (requested === "dark" && pack.dark) return "dark";
  if (requested === "light" && pack.light) return "light";
  if (pack.light) return "light";
  if (pack.dark) return "dark";
  // Schema validation prevents this in practice, but be defensive.
  return requested;
}

/**
 * Apply the given pack to `document.documentElement`. Safe to call from any
 * effect; no-ops cleanly in a non-DOM environment (SSR, vitest jsdom-less).
 */
export function applyTheme(pack: ThemePack, requested: ResolvedMode = "light"): ResolvedMode {
  if (typeof document === "undefined") return requested;

  const root = document.documentElement;
  const mode = resolveMode(pack, requested);
  const modeTokens = mode === "dark" ? pack.dark! : pack.light!;

  // Color + chart variables
  for (const [token, value] of Object.entries(modeTokens.colors)) {
    root.style.setProperty(COLOR_VAR_MAP[token as keyof ColorTokens], value);
  }
  for (const [token, value] of Object.entries(modeTokens.charts)) {
    root.style.setProperty(CHART_VAR_MAP[token as keyof ChartTokens], value);
  }

  // Surface variables
  root.style.setProperty("--radius", pack.surfaces.radius);
  if (pack.surfaces.shadowSm) root.style.setProperty("--shadow-sm", pack.surfaces.shadowSm);
  if (pack.surfaces.shadowMd) root.style.setProperty("--shadow-md", pack.surfaces.shadowMd);
  if (pack.surfaces.shadowLg) root.style.setProperty("--shadow-lg", pack.surfaces.shadowLg);

  // Typography — we set CSS vars and let global styles wire them up. We avoid
  // mutating Tailwind's `font-sans` because the user-authored fonts are loaded
  // lazily via <link>, and we don't want a flash before the file arrives.
  root.style.setProperty("--font-display", pack.typography.displayFont);
  root.style.setProperty("--font-body", pack.typography.bodyFont);
  if (pack.typography.monoFont) {
    root.style.setProperty("--font-mono", pack.typography.monoFont);
  }
  if (pack.typography.displayLetterSpacing) {
    root.style.setProperty("--display-letter-spacing", pack.typography.displayLetterSpacing);
  }

  // Mark which theme is active for CSS hooks like `[data-theme="parchment"] …`
  root.setAttribute("data-theme", pack.meta.slug);
  root.setAttribute("data-theme-mode", mode);

  // Google Fonts <link>
  if (pack.typography.googleFontsUrl) {
    upsertFontsLink(pack.typography.googleFontsUrl);
  } else {
    removeFontsLink();
  }

  // Favicon — runtime swap. We always create our own <link> so we can remove
  // it cleanly in `resetTheme()` without disturbing the static favicons in
  // `index.html`.
  if (pack.assets?.faviconUrl) {
    upsertFavicon(pack.assets.faviconUrl);
  } else {
    removeFavicon();
  }

  return mode;
}

/**
 * Remove every override the SDK has set, returning the page to whatever
 * `index.css` and the static `<head>` declare.
 */
export function resetTheme(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  for (const cssVar of Object.values(COLOR_VAR_MAP)) {
    root.style.removeProperty(cssVar);
  }
  for (const cssVar of Object.values(CHART_VAR_MAP)) {
    root.style.removeProperty(cssVar);
  }
  for (const cssVar of [
    "--radius",
    "--shadow-sm",
    "--shadow-md",
    "--shadow-lg",
    "--font-display",
    "--font-body",
    "--font-mono",
    "--display-letter-spacing",
  ]) {
    root.style.removeProperty(cssVar);
  }
  root.removeAttribute("data-theme");
  root.removeAttribute("data-theme-mode");
  removeFontsLink();
  removeFavicon();
}

function upsertFontsLink(href: string): void {
  let link = document.getElementById(FONTS_LINK_ID) as HTMLLinkElement | null;
  if (link && link.href === href) return; // already correct, nothing to do
  if (!link) {
    link = document.createElement("link");
    link.id = FONTS_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = href;
}

function removeFontsLink(): void {
  const link = document.getElementById(FONTS_LINK_ID);
  if (link) link.remove();
}

function upsertFavicon(href: string): void {
  let link = document.getElementById(FAVICON_LINK_ID) as HTMLLinkElement | null;
  if (link && link.href === href) return;
  if (!link) {
    link = document.createElement("link");
    link.id = FAVICON_LINK_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

function removeFavicon(): void {
  const link = document.getElementById(FAVICON_LINK_ID);
  if (link) link.remove();
}
